import { useState, useEffect, useRef } from 'react';
import { Loader2, Plus, Trash2, ChevronDown, CheckCircle2, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { useFormIntegration, type IFormIntegrationConfig } from '~/hooks/useFormIntegration';
import { useAttioAttributes, type IAttioAttribute } from '~/hooks/useAttioAttributes';
import type { IFormSchema } from '~/types';

interface IFieldMapping {
  formFields: string[];
  attioAttribute: string;
}

interface IAttioFormConfigProps {
  formId: string;
  integrationId: string;
  formSchema: IFormSchema;
}

function MultiSelectFormFields({
  selected,
  options,
  onChange,
}: {
  selected: string[];
  options: string[];
  onChange: (fields: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggle = (field: string) => {
    if (selected.includes(field)) {
      onChange(selected.filter((f) => f !== field));
    } else {
      onChange([...selected, field]);
    }
  };

  const removeField = (field: string) => {
    onChange(selected.filter((f) => f !== field));
  };

  return (
    <div ref={ref} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm min-h-[38px]"
      >
        <span className="flex flex-wrap gap-1 flex-1 text-left">
          {selected.length === 0 && <span className="text-muted-foreground">Select form fields...</span>}
          {selected.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
              {f}
              <X
                className="h-3 w-3 cursor-pointer hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  removeField(f);
                }}
              />
            </span>
          ))}
        </span>
        <ChevronDown
          className={`ml-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-md max-h-48 overflow-y-auto">
          {options.map((f) => (
            <label key={f} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted cursor-pointer">
              <input type="checkbox" checked={selected.includes(f)} onChange={() => toggle(f)} className="rounded" />
              {f}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldMappingRow({
  mapping,
  formFields,
  attioAttributes,
  onChange,
  onRemove,
}: {
  mapping: IFieldMapping;
  formFields: string[];
  attioAttributes: IAttioAttribute[];
  onChange: (updated: IFieldMapping) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <MultiSelectFormFields
        selected={mapping.formFields}
        options={formFields}
        onChange={(formFields) => onChange({ ...mapping, formFields })}
      />
      <span className="text-muted-foreground text-sm shrink-0">maps to</span>
      <select
        className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
        value={mapping.attioAttribute}
        onChange={(e) => onChange({ ...mapping, attioAttribute: e.target.value })}
      >
        <option value="">Select Attio attribute...</option>
        {attioAttributes.map((a) => (
          <option key={a.api_slug} value={a.api_slug}>
            {a.title} ({a.api_slug})
          </option>
        ))}
      </select>
      <Button variant="ghost" size="sm" onClick={onRemove}>
        <Trash2 className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  );
}

/* Migrate old single-field format to multi-field array */
function migrateMapping(m: { formField?: string; formFields?: string[]; attioAttribute: string }): IFieldMapping {
  return {
    formFields: m.formFields ?? (m.formField ? [m.formField] : []),
    attioAttribute: m.attioAttribute,
  };
}

export const AttioFormConfig = ({ formId, integrationId, formSchema }: IAttioFormConfigProps) => {
  const { formIntegration, loading, error, save } = useFormIntegration(formId, integrationId);
  const { personAttributes, companyAttributes, loading: attrsLoading } = useAttioAttributes(true);

  const [enabled, setEnabled] = useState(false);
  const [personMappings, setPersonMappings] = useState<IFieldMapping[]>([]);
  const [companyMappings, setCompanyMappings] = useState<IFieldMapping[]>([]);
  const [syncPartialFills, setSyncPartialFills] = useState(true);
  const [partialFillDelay, setPartialFillDelay] = useState(10);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Initialize state from existing config (with old format migration)
  useEffect(() => {
    if (formIntegration) {
      setEnabled(formIntegration.enabled);
      const config = formIntegration.config;
      setPersonMappings((config.personFieldMappings ?? []).map(migrateMapping));
      setCompanyMappings((config.companyAttributeMappings ?? []).map(migrateMapping));
      setSyncPartialFills(config.syncPartialFills ?? true);
      setPartialFillDelay(config.partialFillDelayMinutes ?? 10);
    }
  }, [formIntegration]);

  const formFields = Object.keys(formSchema.properties ?? {});

  const handleSave = async () => {
    setSaving(true);
    try {
      const config: IFormIntegrationConfig = {
        personFieldMappings: personMappings.filter((m) => m.formFields.length > 0 && m.attioAttribute),
        companyAttributeMappings: companyMappings.filter((m) => m.formFields.length > 0 && m.attioAttribute),
        syncPartialFills,
        partialFillDelayMinutes: partialFillDelay,
      };
      await save(enabled, config);
      setDirty(false);
    } catch {
      // error is set in the hook
    } finally {
      setSaving(false);
    }
  };

  const markDirty = () => setDirty(true);

  const updatePersonMapping = (index: number, updated: IFieldMapping) => {
    const next = [...personMappings];
    next[index] = updated;
    setPersonMappings(next);
    markDirty();
  };

  const updateCompanyMapping = (index: number, updated: IFieldMapping) => {
    const next = [...companyMappings];
    next[index] = updated;
    setCompanyMappings(next);
    markDirty();
  };

  if (loading || attrsLoading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const mappingCount =
    personMappings.filter((m) => m.formFields.length > 0 && m.attioAttribute).length +
    companyMappings.filter((m) => m.formFields.length > 0 && m.attioAttribute).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <CardTitle className="text-base">Attio CRM Sync</CardTitle>
              {!enabled && <CardDescription>Configure how this form syncs to Attio CRM</CardDescription>}
            </div>
            {enabled && !expanded && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-green-700 font-medium">Active</span>
                {mappingCount > 0 && (
                  <span>
                    &middot; {mappingCount} field {mappingCount === 1 ? 'mapping' : 'mappings'}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {enabled && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? 'Collapse' : 'Edit'}
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={async (e) => {
                  const newEnabled = e.target.checked;
                  setEnabled(newEnabled);
                  if (newEnabled) {
                    setExpanded(true);
                    markDirty();
                  } else {
                    setExpanded(false);
                    setSaving(true);
                    try {
                      const config: IFormIntegrationConfig = {
                        personFieldMappings: personMappings.filter((m) => m.formFields.length > 0 && m.attioAttribute),
                        companyAttributeMappings: companyMappings.filter(
                          (m) => m.formFields.length > 0 && m.attioAttribute,
                        ),
                        syncPartialFills,
                        partialFillDelayMinutes: partialFillDelay,
                      };
                      await save(false, config);
                      setDirty(false);
                    } catch {
                      /* error is set in the hook */
                    } finally {
                      setSaving(false);
                    }
                  }
                }}
                className="rounded"
              />
              <span className="text-sm font-medium">Enabled</span>
            </label>
          </div>
        </div>
      </CardHeader>

      {enabled && expanded && (
        <CardContent className="space-y-6">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Person Field Mappings */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Person Field Mappings</h4>
            {personMappings.map((mapping, i) => (
              <FieldMappingRow
                key={i}
                mapping={mapping}
                formFields={formFields}
                attioAttributes={personAttributes}
                onChange={(u) => updatePersonMapping(i, u)}
                onRemove={() => {
                  setPersonMappings(personMappings.filter((_, j) => j !== i));
                  markDirty();
                }}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPersonMappings([...personMappings, { formFields: [], attioAttribute: '' }]);
                markDirty();
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> Add Person Mapping
            </Button>
          </div>

          {/* Company Field Mappings */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Company Field Mappings</h4>
            {companyMappings.map((mapping, i) => (
              <FieldMappingRow
                key={i}
                mapping={mapping}
                formFields={formFields}
                attioAttributes={companyAttributes}
                onChange={(u) => updateCompanyMapping(i, u)}
                onRemove={() => {
                  setCompanyMappings(companyMappings.filter((_, j) => j !== i));
                  markDirty();
                }}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCompanyMappings([...companyMappings, { formFields: [], attioAttribute: '' }]);
                markDirty();
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> Add Company Mapping
            </Button>
          </div>

          {/* Partial Fill Settings */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Partial Fill Sync</h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={syncPartialFills}
                onChange={(e) => {
                  setSyncPartialFills(e.target.checked);
                  markDirty();
                }}
                className="rounded"
              />
              <span className="text-sm">Sync incomplete submissions after inactivity</span>
            </label>
            {syncPartialFills && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Delay:</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={partialFillDelay}
                  onChange={(e) => {
                    setPartialFillDelay(Number(e.target.value));
                    markDirty();
                  }}
                  className="w-20 rounded-md border bg-background px-3 py-1 text-sm"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={async () => {
                await handleSave();
                setExpanded(false);
              }}
              disabled={saving || !dirty}
              size="sm"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
